import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '@/App';
import { Button } from '@/components/ui/button';
import LivePolicyWidget from '@/components/LivePolicyWidget';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Shield, 
  Bell, 
  BarChart3, 
  FileText, 
  Globe, 
  Users, 
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Zap,
  Home,
  Train,
  ShoppingBag,
  Building2,
  Leaf,
  FileWarning
} from 'lucide-react';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'ar', name: 'العربية' },
  { code: 'ht', name: 'Kreyòl Ayisyen' }
];

const Landing = () => {
  const { user } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();

  const features = [
    {
      icon: Home,
      title: language === 'es' ? 'Vivienda y Robo de Escrituras' : 'Housing & Deed Theft',
      description: language === 'es' 
        ? 'Alertas sobre estabilización de alquiler, derechos de inquilinos y protección contra robo de escrituras'
        : 'Alerts on rent stabilization, tenant rights, and deed theft protection'
    },
    {
      icon: Train,
      title: language === 'es' ? 'Transporte' : 'Transportation',
      description: language === 'es'
        ? 'Seguimiento de cambios en MTA, precios de congestión y rutas de autobús'
        : 'Track MTA changes, congestion pricing, and bus route updates'
    },
    {
      icon: ShoppingBag,
      title: language === 'es' ? 'Acceso a Alimentos' : 'Food Access',
      description: language === 'es'
        ? 'Monitoreo de desiertos alimentarios, SNAP y políticas de supermercados'
        : 'Monitor food deserts, SNAP benefits, and grocery store policies'
    },
    {
      icon: Building2,
      title: language === 'es' ? 'Pequeñas Empresas' : 'Small Business',
      description: language === 'es'
        ? 'Subvenciones, regulaciones y políticas de vacantes de tiendas'
        : 'Grants, regulations, and storefront vacancy policies'
    },
    {
      icon: Leaf,
      title: language === 'es' ? 'Justicia Ambiental' : 'Environmental Justice',
      description: language === 'es'
        ? 'Calidad del aire, instalaciones de residuos y espacios verdes'
        : 'Air quality, waste facilities, and green space policies'
    },
    {
      icon: FileWarning,
      title: language === 'es' ? 'Alertas de Robo de Escrituras' : 'Deed Theft Alerts',
      description: language === 'es'
        ? 'Sistema de alerta dedicado para proteger la propiedad de tu hogar'
        : 'Dedicated alert system to protect your home ownership'
    }
  ];

  const stats = [
    { value: '5', label: language === 'es' ? 'Distritos NYC' : 'NYC Boroughs' },
    { value: '21+', label: language === 'es' ? 'Vecindarios EJ' : 'EJ Neighborhoods' },
    { value: '6', label: language === 'es' ? 'Categorías' : 'Policy Categories' },
    { value: '24/7', label: language === 'es' ? 'Monitoreo' : 'Monitoring' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary" />
              <div className="flex flex-col">
                <span className="font-bold text-xl tracking-tight leading-none">PoliBot</span>
                <span className="text-[10px] text-muted-foreground hidden sm:block">NYC Environmental Justice</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="language-toggle"
                  >
                    <Globe className="h-4 w-4" />
                    {language.toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map(lang => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)} 
                      className={language === lang.code ? 'bg-muted' : ''}
                    >
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              {user ? (
                <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
                  {t('nav.dashboard')}
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => navigate('/login')} data-testid="login-btn">
                    {t('nav.login')}
                  </Button>
                  <Button onClick={() => navigate('/register')} data-testid="register-btn">
                    {t('nav.register')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Zap className="h-4 w-4" />
                {language === 'es' ? 'NYC • Justicia Ambiental' : 'NYC • Environmental Justice'}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-6 text-foreground">
                {language === 'es' 
                  ? 'Rastreando Políticas que Afectan a Nuestras Comunidades' 
                  : 'Tracking Policies that Affect Our Communities the Most'}
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-xl">
                {language === 'es'
                  ? 'Plataforma de justicia ambiental para comunidades de NYC. Monitorea vivienda, transporte, acceso a alimentos, pequeñas empresas y alertas de robo de escrituras.'
                  : 'Environmental justice platform for NYC communities. Monitor housing, transportation, food access, small business policies, and deed theft alerts.'}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="group"
                  data-testid="get-started-btn"
                >
                  {t('landing.cta')}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="learn-more-btn"
                >
                  {t('landing.learnMore')}
                </Button>
              </div>
            </div>
            
            <div className="relative animate-fade-in">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ecowatch-6/artifacts/w9irsoub_istockphoto-1425962342-612x612.jpg"
                  alt="Diverse community organizers collaborating"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="glass-card rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-accent" />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {language === 'es' ? 'Nueva Alerta de Política' : 'New Policy Alert'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {language === 'es' ? 'Revisión de calidad del aire EPA' : 'EPA Air Quality Standard Revision'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NYC Focus Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4">
              {language === 'es' 
                ? 'Enfocado en los 5 Distritos de NYC' 
                : 'Focused on All 5 NYC Boroughs'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'es'
                ? 'Monitoreamos políticas que afectan comunidades de justicia ambiental en todo Nueva York.'
                : 'We monitor policies affecting environmental justice communities across New York City.'}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {['Bronx', 'Brooklyn', 'Manhattan', 'Queens', 'Staten Island'].map((borough, index) => (
              <div 
                key={borough}
                className="bg-card rounded-lg border border-border/60 p-4 text-center transition-all hover:border-primary/30 hover:shadow-md animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-medium text-sm">{borough}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Policy Alerts Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="animate-slide-up">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4">
                {language === 'es' 
                  ? 'Monitoreo en Tiempo Real' 
                  : 'Real-Time Policy Monitoring'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {language === 'es'
                  ? 'Rastreamos múltiples fuentes incluyendo el Concejo Municipal de NYC, la Oficina del Alcalde, la Legislatura del Estado de NY y el Registro Federal.'
                  : 'We track multiple sources including NYC Council, Mayor\'s Office, NY State Legislature, and the Federal Register.'}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">
                    {language === 'es' 
                      ? 'Legislación del Concejo Municipal de NYC' 
                      : 'NYC Council legislation'}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">
                    {language === 'es' 
                      ? 'Anuncios de la Oficina del Alcalde' 
                      : 'Mayor\'s Office announcements'}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">
                    {language === 'es' 
                      ? 'Proyectos de ley de la Legislatura del Estado de NY' 
                      : 'NY State Legislature bills'}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">
                    {language === 'es' 
                      ? 'Agendas de reuniones de Juntas Comunitarias' 
                      : 'Community Board meeting agendas'}
                  </span>
                </li>
              </ul>
              <Button 
                className="mt-6"
                onClick={() => navigate('/register')}
                data-testid="live-alerts-cta-btn"
              >
                {language === 'es' ? 'Obtener Alertas' : 'Get Alerts'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="animate-fade-in">
              <LivePolicyWidget language={language} maxItems={4} showRefresh={true} />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              {language === 'es' ? 'Características Potentes' : 'Powerful Features'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'es' 
                ? 'Todo lo que necesitas para monitorear políticas y proteger a tu comunidad'
                : 'Everything you need to monitor policies and protect your community'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-card rounded-lg border border-border/60 p-6 transition-all hover:border-primary/20 hover:-translate-y-1 hover:shadow-lg animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 transition-colors group-hover:bg-primary/20">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deed Theft Alert Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-accent/5 border-y border-accent/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4">
                <FileWarning className="h-4 w-4" />
                {language === 'es' ? 'Protección de Propiedad' : 'Property Protection'}
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4">
                {language === 'es' 
                  ? 'Sistema de Alerta de Robo de Escrituras' 
                  : 'Deed Theft Alert System'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {language === 'es'
                  ? 'Protege tu propiedad con nuestro sistema dedicado de alertas de robo de escrituras. Recibe notificaciones sobre cambios sospechosos y accede a recursos para protegerte.'
                  : 'Protect your property with our dedicated deed theft alert system. Get notified about suspicious changes and access resources to protect yourself.'}
              </p>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-accent hover:bg-accent/90"
                data-testid="deed-theft-cta-btn"
              >
                {language === 'es' ? 'Proteger Mi Propiedad' : 'Protect My Property'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="bg-card rounded-lg border border-border/60 p-6 animate-fade-in">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-accent" />
                {language === 'es' ? 'Qué Monitoreamos' : 'What We Monitor'}
              </h3>
              <ul className="space-y-3">
                {[
                  language === 'es' ? 'Transferencias de propiedad sospechosas' : 'Suspicious property transfers',
                  language === 'es' ? 'Cambios en registros de escrituras' : 'Deed record changes',
                  language === 'es' ? 'Políticas que afectan derechos de propiedad' : 'Policies affecting property rights',
                  language === 'es' ? 'Recursos de asistencia legal' : 'Legal assistance resources'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
            {language === 'es' 
              ? '¿Listo para proteger a tu comunidad de NYC?'
              : 'Ready to protect your NYC community?'}
          </h2>
          <p className="text-primary-foreground/80 mb-8 text-lg">
            {language === 'es'
              ? 'Únete a organizaciones de justicia ambiental de los 5 distritos que ya usan PoliBot'
              : 'Join environmental justice organizations from all 5 boroughs already using PoliBot'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="secondary"
              onClick={() => navigate('/register')}
              className="group"
              data-testid="cta-get-started-btn"
            >
              {t('landing.cta')}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              <div>
                <span className="font-bold">PoliBot</span>
                <p className="text-xs text-muted-foreground">
                  {language === 'es' 
                    ? 'Rastreando políticas que afectan a nuestras comunidades' 
                    : 'Tracking policies that affect our communities'}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 PoliBot. {language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
